import {
  ElementRef,
  forwardRef,
  ForwardRefExoticComponent,
  ForwardRefRenderFunction,
  MutableRefObject,
  ReactElement,
  RefAttributes,
  useRef
} from 'react'

/** 親コンポーネントに渡される ref */
type HandlerRef<T> =
  | ((instance: T | null) => void)
  | MutableRefObject<T | null>
  | null

/** 子コンポーネントで公開されたメソッドをuseRefに渡される */
type UseRefHandler<T> = ElementRef<ForwardRefExoticComponent<RefAttributes<T>>>

/**
 * FunctionComponent を ref でアクセスできるようにします。
 * @param funcComp ref を付与したい関数コンポーネント
 * @example
 *   // ref で公開したいメソッド
 *   export type Handler = {
 *     hello: () => void;
 *   };
 *   // 公開する側のコンポーネント
 *   export const Child = withRefFC<Handler>((ref, props) => {
 *     const [text, setText] = useState('');
 *
 *     // 公開するメソッドの定義
 *     useImperativeHandle(ref, () => ({
 *       hello: () => {
 *         alert(`Hello ${text}!`);
 *       },
 *     }), [text]);
 *
 *     return (
 *       <input type="text" value={text} onChange={(e) => {
 *         setText(e.target.value);
 *       }} />
 *     );
 *   });
 *
 *  @see useHandlerRef 使用する側のHOOK
 */
export const withRefFC = <Handler, Props = {}>(
  funcComp: (ref: HandlerRef<Handler>, props: Props) => ReactElement | null
) => {
  const HoC: ForwardRefRenderFunction<Handler, Props> = (p, r) => funcComp(r, p)
  return forwardRef(HoC)
}

/**
 * 使用する側のコンポーネント
 *  @example
 *   import {Handler, Child} from "withRefFunctionComponent"
 *   export const Parent: FC = () => {
 *     const handlerRef = useHandlerRef<Handler>();
 *     return (
 *       <div>
 *         <Child ref={handlerRef} />
 *         <button onClick={() => {
 *           handlerRef.current?.hello();
 *         }}>クリック！</button>
 *       </div>
 *     );
 *   };
 */
export const useHandlerRef = <Handler>() => useRef<UseRefHandler<Handler>>(null)
